import { TwilioVoiceService } from './TwilioVoiceService'
import { MockVoiceService } from './MockVoiceService'
import { VoiceService } from './types'

/**
 * Voice Service Factory
 *
 * This factory creates voice service instances based on configuration.
 * Supports Twilio for production and Mock for testing/development.
 */

export type VoiceServiceProvider = 'twilio' | 'mock'

export interface VoiceServiceConfig {
  provider: VoiceServiceProvider
  getNewToken?: () => Promise<string>
}

export function createVoiceService(config: VoiceServiceConfig): VoiceService {
  switch (config.provider) {
    case 'twilio':
      return new TwilioVoiceService(config.getNewToken)
    case 'mock':
      return new MockVoiceService()
    default:
      throw new Error(`Unsupported voice service provider: ${config.provider}`)
  }
}

// Determine voice service provider from env var or default based on environment
function getVoiceProvider(): VoiceServiceProvider {
  const envProvider = import.meta.env.VITE_VOICE_SERVICE as string | undefined

  if (envProvider === 'twilio' || envProvider === 'mock') {
    return envProvider
  }

  // Default: mock in development, twilio in production
  return import.meta.env.DEV ? 'mock' : 'twilio'
}

// Current provider (cached)
const currentProvider = getVoiceProvider()

// Debug log
console.log('[VoiceService] Provider:', currentProvider, '| DEV:', import.meta.env.DEV, '| ENV:', import.meta.env.VITE_VOICE_SERVICE)

// Default configuration
export const defaultVoiceConfig: VoiceServiceConfig = {
  provider: currentProvider
}

// Check if using mock service
export const isMockVoiceService = currentProvider === 'mock'

// Export types for consumers
export * from './types'
export { TwilioVoiceService, MockVoiceService }
